'use client'

import React, { useEffect, useMemo } from 'react'
import { Search, SlidersHorizontal } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { cn } from '@/utilities/ui'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { MultiWillayaSelect } from '@/components/ui/multi-willaya-select'
import { MultiCommuneSelect } from '@/components/ui/multi-commune-select'
import { useTheme } from '@/providers/Theme'

interface AsideFilterComponentProps {
  className?: string
  search: string
  handleSearchChange: (value: string) => void
  listedIn: string
  selectedCategories: string[]
  toggleCategory: (value: string) => void
  onListedInChange: (value: string) => void
  minPrice: string | number
  onMinPriceChange: (value: string) => void
  maxPrice: string | number
  onMaxPriceChange: (value: string) => void
  minSurface: string | number
  onMinSurfaceChange: (value: string) => void
  maxSurface: string | number
  onMaxSurfaceChange: (value: string) => void
  rooms: string[]
  onRoomsChange: (value: string[]) => void
  amenities: string[]
  toggleAmenity: (value: string) => void
  updateSearchParams: () => void
  onReset: () => void
  willayaList: any[]
  setSelectedWillayas: (value: string[]) => void
  selectedWillayas: string[]
  isWillayaPopoverOpen: boolean
  setIsWillayaPopoverOpen: (value: boolean) => void
  citiesList: any[]
  setSelectedCities: (value: string[]) => void
  selectedCities: string[]
  isCityPopoverOpen: boolean
  setIsCityPopoverOpen: (value: boolean) => void
  isOpenOtherFeatures: boolean
  setIsOpenOtherFeatures: (value: boolean) => void
  showAdvancedSearch: boolean
  setShowAdvancedSearch: (value: boolean) => void
  PropertyCategories: any[]
  roomOptions: any[]
  FeatureSearchList: any[]
  lang?: string // Added language parameter
  dictionary?: any // Added dictionary parameter
}

export default function AsideFilterComponent({
  className,
  search,
  handleSearchChange,
  listedIn,
  selectedCategories,
  toggleCategory,
  onListedInChange,
  minPrice,
  onMinPriceChange,
  maxPrice,
  onMaxPriceChange,
  minSurface,
  onMinSurfaceChange,
  maxSurface,
  onMaxSurfaceChange,
  rooms,
  onRoomsChange,
  amenities,
  toggleAmenity,
  updateSearchParams,
  onReset,
  willayaList,
  setSelectedWillayas,
  selectedWillayas,
  isWillayaPopoverOpen,
  setIsWillayaPopoverOpen,
  citiesList,
  setSelectedCities,
  selectedCities,
  isCityPopoverOpen,
  setIsCityPopoverOpen,
  isOpenOtherFeatures,
  setIsOpenOtherFeatures,
  showAdvancedSearch,
  setShowAdvancedSearch,
  PropertyCategories,
  roomOptions,
  FeatureSearchList,
  lang = 'en',
  dictionary = {},
}: AsideFilterComponentProps) {
  const { theme } = useTheme()

  // Check if RTL
  const isRTL = lang === 'ar'

  // Extract translations or use defaults if not provided
  const t = {
    findYourHome: dictionary?.asideFilter?.findYourHome || 'Find your home',
    whatAreYouLookingFor:
      dictionary?.asideFilter?.whatAreYouLookingFor || 'What are you looking for?',
    listingStatus: dictionary?.asideFilter?.listingStatus || 'Listing Status',
    all: dictionary?.asideFilter?.all || 'all',
    buy: dictionary?.asideFilter?.buy || 'Buy',
    rent: dictionary?.asideFilter?.rent || 'Rent',
    propertyType: dictionary?.asideFilter?.propertyType || 'Property Type',
    priceRange: dictionary?.asideFilter?.priceRange || 'Price Range (DZD)',
    minPrice: dictionary?.asideFilter?.minPrice || '$ min.',
    maxPrice: dictionary?.asideFilter?.maxPrice || '$ max.',
    willaya: dictionary?.asideFilter?.willaya || 'Willaya',
    selectWillaya: dictionary?.asideFilter?.selectWillaya || 'Select willaya options',
    city: dictionary?.asideFilter?.city || 'City',
    selectCity: dictionary?.asideFilter?.selectCity || 'Select city options',
    surface: dictionary?.asideFilter?.surface || 'Surface (m²)',
    min: dictionary?.asideFilter?.min || 'Min.',
    max: dictionary?.asideFilter?.max || 'Max.',
    rooms: dictionary?.asideFilter?.rooms || 'Rooms',
    otherFeatures: dictionary?.asideFilter?.otherFeatures || 'Other Features',
    amenities: dictionary?.asideFilter?.amenities || 'Amenities',
    search: dictionary?.asideFilter?.search || 'Search',
    resetAllFilters: dictionary?.asideFilter?.resetAllFilters || 'Reset all filters',
  }

  const handleRoomsChange = (value: string) => {
    // Clicking "Any" clears all selections
    if (value === 'any') {
      onRoomsChange([])
      return
    }

    const v = String(value)

    const nextRooms = rooms.includes(v)
      ? rooms.filter((x) => x !== v) // remove
      : [...rooms, v] // add

    // Keep numeric order for stable URLs / state
    nextRooms.sort((a, b) => Number(a) - Number(b))

    onRoomsChange(nextRooms)
  }

  return (
    <div className={cn(className)}>
      {/* Search Box */}
      <div className="flex flex-col space-y-[10px]">
        <h2 className={cn('text-typography text-[15px] font-semibold mb-4', isRTL && 'text-right')}>
          {t.findYourHome}
        </h2>

        <div className="relative">
          <Input
            type="text"
            placeholder={t.whatAreYouLookingFor}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className={cn('h-[55px]', isRTL ? 'pr-10 text-right' : 'pl-10')}
          />
          <button
            type="submit"
            className={cn(
              'absolute top-1/2 transform -translate-y-1/2 text-muted',
              isRTL ? 'right-3' : 'left-3',
            )}
          >
            <Search size={18} />
          </button>
        </div>
      </div>

      {/* Listing Status */}
      <div className="flex flex-col space-y-[10px]">
        <h3 className={cn('text-typography text-sm font-semibold', isRTL && 'text-right')}>
          {t.listingStatus}
        </h3>
        <RadioGroup
          onValueChange={(value) => onListedInChange(value)}
          defaultValue=""
          value={listedIn}
          className="space-y-2"
        >
          <div
            className={cn(
              'text-typography flex items-center',
              isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2',
            )}
          >
            <RadioGroupItem value="" id="r1" />
            <Label htmlFor="r1">{t.all}</Label>
          </div>
          <div
            className={cn(
              'text-typography flex items-center',
              isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2',
            )}
          >
            <RadioGroupItem value="for_sale" id="r2" />
            <Label htmlFor="r2">{t.buy}</Label>
          </div>
          <div
            className={cn(
              'text-typography flex items-center',
              isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2',
            )}
          >
            <RadioGroupItem value="for_rent" id="r3" />
            <Label htmlFor="r3">{t.rent}</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Property Type */}
      <div className="flex flex-col space-y-[10px]">
        <h3 className={cn('text-typography text-sm font-semibold', isRTL && 'text-right')}>
          {t.propertyType}
        </h3>
        <div className="space-y-4">
          {PropertyCategories?.map((category) => (
            <div key={category.id} className={cn('text-typography flex items-center', isRTL && '')}>
              <Checkbox
                checked={selectedCategories.includes(category.value)}
                onCheckedChange={() => toggleCategory(category.value)}
                id={category.value}
                className={cn(isRTL ? 'ml-2' : 'mr-2')}
              />
              <Label htmlFor={category.value}>{category.title}</Label>
            </div>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div className="flex flex-col space-y-[10px]">
        <h3 className={cn('text-typography text-sm font-semibold', isRTL && 'text-right')}>
          {t.priceRange}
        </h3>
        <div className={cn('flex items-center gap-2', isRTL && 'flex-row-reverse')}>
          <Input
            value={minPrice}
            onChange={(e) => onMinPriceChange(e.target.value)}
            type="number"
            placeholder={t.minPrice}
            className={cn('h-[55px] w-full', isRTL && 'text-right')}
            min={0}
          />
          <span className="text-typography">—</span>
          <Input
            value={maxPrice}
            onChange={(e) => onMaxPriceChange(e.target.value)}
            type="number"
            placeholder={t.maxPrice}
            className={cn('h-[55px] w-full', isRTL && 'text-right')}
            min={0}
          />
        </div>
      </div>

      {/* Location */}
      <div className="!relative space-y-[10px]">
        <Label className={cn('text-typography font-semibold', isRTL && 'text-right block')}>
          {t.willaya}
        </Label>
        <MultiCommuneSelect
          options={willayaList}
          onValueChange={(e) => {
            setSelectedWillayas(e)
          }}
          value={selectedWillayas}
          initialSelectedValues={selectedWillayas}
          placeholder={t.selectWillaya}
          variant="default"
          animation={2}
          maxCount={3}
          isPopoverOpen={isWillayaPopoverOpen}
          setIsPopoverOpen={setIsWillayaPopoverOpen}
          className="!bg-transparent"
        />
      </div>
      <div className="!relative space-y-[10px]">
        <Label className={cn('text-typography font-medium', isRTL && 'text-right block')}>
          {t.city}
        </Label>
        <MultiCommuneSelect
          options={citiesList}
          onValueChange={setSelectedCities}
          value={selectedCities}
          initialSelectedValues={selectedCities}
          placeholder={t.selectCity}
          variant="default"
          animation={2}
          maxCount={3}
          isPopoverOpen={isCityPopoverOpen}
          setIsPopoverOpen={setIsCityPopoverOpen}
          className="!bg-transparent"
        />
      </div>

      {/* Surface Range */}
      <div className="flex flex-col space-y-[10px]">
        <h3 className={cn('text-typography text-sm font-semibold mb-2', isRTL && 'text-right')}>
          {t.surface}
        </h3>
        <div className={cn('flex items-center gap-2', isRTL && 'flex-row-reverse')}>
          <Input
            value={minSurface}
            onChange={(e) => onMinSurfaceChange(e.target.value)}
            min={0}
            type="number"
            placeholder={t.min}
            className={cn('w-full h-[55px]', isRTL && 'text-right')}
          />
          <span className="text-typography">—</span>
          <Input
            value={maxSurface}
            onChange={(e) => onMaxSurfaceChange(e.target.value)}
            min={0}
            type="number"
            placeholder={t.max}
            className={cn('w-full h-[55px]', isRTL && 'text-right')}
          />
        </div>
      </div>

      {/* Rooms Filter */}
      <div className="flex flex-col space-y-[10px]">
        <h3 className={cn('text-typography text-sm font-semibold mb-2', isRTL && 'text-right')}>
          {t.rooms}
        </h3>
        <div className={cn('flex flex-wrap gap-1', isRTL && '')}>
          {roomOptions.map((option) => {
            const isAnySelected = rooms.length === 0

            const isSelected =
              option.value === 'any'
                ? isAnySelected
                : rooms.includes(String(option.value))
            return (
              <button
                key={option.value}
                className={cn(
                  'text-typography flex-1 py-2 px-3 text-center rounded-md transition-colors border-2 text-sm',
                  isSelected
                    ? 'border-typography z-20'
                    : 'bg-inputBackground hover:bg-light border-slate-200/50 z-10',
                  // Smaller text for RTL
                  isRTL && 'text-sm px-2',
                )}
                onClick={() => handleRoomsChange(String(option.value))}
                >
                  {option.label === 'Any' || option.label === 'Tous' || option.label === 'أي'
                    ? option.label
                    : isRTL
                      ? option.label
                      : 'F' + option.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Other Features */}
      <div className="w-full">
        <Collapsible open={isOpenOtherFeatures} onOpenChange={setIsOpenOtherFeatures} className="">
          <CollapsibleTrigger className="w-full border-0">
            <button
              className={cn(
                'flex items-center text-gray-700 text-sm gap-2',
                isRTL && 'flex-row-reverse',
              )}
              onClick={() => setShowAdvancedSearch(true)}
            >
              <div className="flex items-center justify-center h-10 w-10 bg-stone-100 p-2 rounded-full">
                <SlidersHorizontal size={22} className="" />
              </div>
              <span className="text-typography">{t.otherFeatures}</span>
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-6 overflow-hidden transition-all duration-300 ease-in-out">
            <div className="space-y-4">
              <h3
                className={cn('text-typography text-sm font-semibold mb-2', isRTL && 'text-right')}
              >
                {t.amenities}
              </h3>
              {FeatureSearchList.map((feature) => (
                <div key={feature.value} className={cn('flex items-center', isRTL && '')}>
                  <Checkbox
                    checked={amenities.includes(feature.value)}
                    onCheckedChange={() => toggleAmenity(feature.value)}
                    id={feature.value}
                    className={cn(isRTL ? 'ml-2' : 'mr-2')}
                  />
                  <Label className="text-typography" htmlFor={feature.value}>
                    {feature.feature}
                  </Label>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Action Buttons */}
      <div className="hidden md:block space-y-3">
        <Button
          className="w-full h-[55px] bg-primary-300 hover:bg-primary-400 text-white"
          onClick={updateSearchParams}
        >
          <Search size={16} className={cn(isRTL ? 'ml-2' : 'mr-2')} />
          {t.search}
        </Button>
        <Button
          variant="link"
          onClick={onReset}
          className={cn(
            'mx-auto flex items-center text-typography justify-center md:justify-start dark:hover:!text-gray-200 transition-colors duration-200',
            isRTL && 'flex-row-reverse md:justify-end',
          )}
        >
          <img
            src={theme === 'dark' ? '/icons/back-w.svg' : '/icons/back.svg'}
            alt="back svg"
            className={cn('fill-white stroke-white', isRTL ? 'ml-2 scale-x-[-1]' : 'mr-2')}
          />
          {t.resetAllFilters}
        </Button>
      </div>
    </div>
  )
}
